/** @jsxImportSource @emotion/react */
import { OutputData } from "@editorjs/editorjs";
import { Backdrop, CircularProgress, css } from "@mui/material";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTime } from "luxon";
import { useSnackbar } from "notistack";
import React, {
  ChangeEvent,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  VFC,
} from "react";
import { useDropzone } from "react-dropzone";
import { createReactEditorJS } from "react-editor-js";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { BUSINESS_CATEGORIES } from "src/constants/businessCategories";
import { CountryPath } from "src/entities/CountryPath";
import { GetArticlesResponse } from "src/entities/GetArticlesResponse";
import { CreditInfo } from "src/entities/v3/AuthMeResponse";
import { GetArticleResponse } from "src/entities/v3/GetArticleResponse";
import { PublisherResponseV3 } from "src/entities/v3/PublisherResponse";
import { Auth } from "src/hooks/useAuth";
import { Refetch, useFetch } from "src/hooks/useFetch";
import { useUpdateArticle } from "src/hooks/useUpdateArticle";
import { checkSubscripted } from "src/utils/checkSubscripted";
import ComfirmArticleStep from "../components/editArticle/ComfirmArticleStep";
import CreateArticleStep from "../components/editArticle/CreateArticleStep";
import CreateCompleteStep from "../components/editArticle/CreateCompleteStep";
// import DistributionSettingStep from "../components/editArticle/DistributionSettingStep";
import PaymentArticleStep from "../components/editArticle/PaymentArticleStep";
import CreateArticleModal from "../components/modal/CreateArticleModal";
import DateValidationModal from "../components/modal/DateValidationModal";
import FinalConfirmationModal from "../components/modal/FinalConfirmationModal";
import SaveArticleModal from "../components/modal/SaveArticleModal";
import ScheduleArticleModal from "../components/modal/ScheduleArticleModal";
import StepPageWrapper from "../components/page/StepPageWrapper";

interface Props {
  auth: Auth;
  country: CountryPath;
  publisherResponse?: PublisherResponseV3;
  credits?: number;
  creditInfo?: CreditInfo[];
  refetchPublisher: Refetch<PublisherResponseV3>;
  remaining_quota?: number;
  defaultCountry?: string;
}

type Step = "CREATE" | "CONFIRM" | "PAYMENT" | "COMPLETE" | "DISTRIBUTION";

const STEPPER_HEIGHT = 80;

const EditArticlePage: VFC<Props> = (props: Props) => {
  /**
   * Common
   */
  const navigate = useNavigate();
  const [t] = useTranslation();

  /**
   * Get path parameters
   */
  const params = useParams();

  /**
   * Step state
   */
  const [currentStep, setCurrentStep] = useState<Step>(
    params.step === "DISTRIBUTION" ? "DISTRIBUTION" : "CREATE"
  );
  /**
   * Go to confirm step
   */
  const goToConfirmStep = () => {
    setCurrentStep("CONFIRM");
  };
  /**
   * Go back to dashboard
   */
  const goBackToDashboard = () => {
    navigate("/dashboard");
  };
  /**
   * Go to confirm step
   */
  const goBackToCreateStep = () => {
    setCurrentStep("CREATE");
  };
  /**
   * Go back to confirm step
   */
  const goBackToConfirmStep = () => {
    setCurrentStep("CONFIRM");
  };
  /**
   * Go to payment step
   */
  const goToPaymentStep = () => {
    setCurrentStep("PAYMENT");
  };
  /**
  /**
   * Go to compete step
   */
  const goToCompleteStep = () => {
    setCurrentStep("COMPLETE");
  };

  /**
   * Get selected(editing) article
   */
  const { data: articleRaw } = useFetch<GetArticleResponse>({
    url: `${process.env.REACT_APP_DOMAIN_V3}/api/v3/articles/${params.slug}`,
    headers: {
      country: props.country,
    },
  });

  // Parse body from JSON string to OutputData and convert to GetArticleV2 format for compatibility
  // Use useMemo to prevent infinite loop in useEffect
  const article = useMemo(() => {
    if (!articleRaw) return undefined;

    return {
      articles: {
        id: articleRaw.id,
        publisher_id: articleRaw.publisherId,
        title: articleRaw.title,
        state: articleRaw.state,
        body: JSON.parse(articleRaw.body) as OutputData,
        summary: articleRaw.summary,
        thumbnail_image_url: articleRaw.thumbnailImageUrl,
        use_thumbnail_as_featured_image: articleRaw.useThumbnailAsFeaturedImage,
        company_info: articleRaw.companyInfo,
        media_contact: articleRaw.mediaContact,
        country: articleRaw.country,
        published_at: articleRaw.publishedAt,
        created_at: articleRaw.createdAt,
        updated_at: articleRaw.updatedAt,
      },
      business_category: articleRaw.businessCategories.map((name) => ({
        id: "", // Not available in v3
        name: name,
        name_en: "", // Not available in v3
      })),
    };
  }, [articleRaw]);

  /**
   * Set the selected article information
   */
  useEffect(() => {
    if (article && article.articles) {
      setTitle(article.articles.title);
      setPublishedAt(DateTime.fromISO(article.articles.published_at));
      setOutputBody(article.articles.body);
      setSummary(article.articles.summary);
      setMediaContact(article.articles.media_contact);
      setCompanyInfo(article.articles.company_info);
      setSelectedCategories(
        article.business_category.map((category) => category.name)
      );
      setThumbnailAsDefault(article.articles.use_thumbnail_as_featured_image);
    }
  }, [article]);

  /**
   * PR Title settings
   */
  /**
   * PR Title parameters
   */
  const [title, setTitle] = useState("");
  /**
   * Handle the title
   * @param value PR title
   */
  const handleTitleChange = (value: string) => {
    setTitle(value);
  };

  /**
   * PR Summary settings
   */
  /**
   * PR Summary parameters
   */
  const [summary, setSummary] = useState("");
  /**
   * Handle the summary
   * @param value PR summary
   */
  const handleSummaryChange = (value: string) => {
    setSummary(value);
  };

  /**
   * Published at settings
   */
  /**
   * Published at parameters
   */
  const [publishedAt, setPublishedAt] = useState(DateTime.now());
  /**
   * Handle published at
   * @param value post date
   */
  const handlePublishedAtChange = (value: DateTime) => {
    setPublishedAt(value);
  };

  /**
   * Snackbar
   */
  const { enqueueSnackbar } = useSnackbar();

  /**
   * Image file hooks
   */
  const { acceptedFiles, getRootProps, getInputProps, isDragAccept } =
    useDropzone({
      multiple: false,
      maxSize: 5 * 1024 * 1024,
      accept: {
        "image/jpeg": [".jpg", ".jpeg"],
        "image/png": [".png"],
        "image/webp": [".webp"],
        "image/gif": [".gif"],
        "image/svg+xml": [".svg"],
      },
      onDropRejected: (fileRejections) => {
        const rejection = fileRejections[0];
        if (rejection?.errors?.[0]?.code === "file-too-large") {
          enqueueSnackbar("File size exceeds the 5MB limit", {
            variant: "error",
          });
        } else if (rejection?.errors?.[0]?.code === "file-invalid-type") {
          enqueueSnackbar(
            "Invalid file type. Only JPEG, PNG, WebP, GIF, and SVG images are allowed.",
            {
              variant: "error",
            }
          );
        } else {
          enqueueSnackbar("File upload failed", {
            variant: "error",
          });
        }
      },
    });

  /**
   * Thumbnail flag settings
   */
  /**
   * Thumbnail flag parameters
   */
  const [thumbnailAsDefault, setThumbnailAsDefault] = useState(false);
  /**
   * Handle thumbnail flag
   */
  const handleThumbnailAsDefault = (e: ChangeEvent<HTMLInputElement>) => {
    setThumbnailAsDefault(e.target.checked);
  };

  /**
   * Business categories settings
   */
  /**
   * Business categories parameters
   */
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  /**
   * Handle business categories
   */
  const handleBusinessCategories = (
    _: SyntheticEvent<Element, Event>,
    values: string[]
  ) => {
    if (values.length > 5) {
      return;
    }
    setSelectedCategories(values);
  };

  /**
   * Company Info settings
   */
  /**
   * Company Info parameters
   */
  const [companyInfo, setCompanyInfo] = useState("");
  /**
   * Handle company info
   * @param value company info
   */
  const handleCompanyInfo = (value: string) => {
    setCompanyInfo(value);
  };

  /**
   * Media contact settings
   */
  /**
   * Media contact parameters
   */
  const [mediaContact, setMediaContact] = useState("");
  /**
   * Handle media contact
   * @param value media contact
   */
  const handleMediaContact = (value: string) => {
    setMediaContact(value);
  };

  /**
   * Dropdown settings
   */
  /**
   * Dropdown parameters
   */
  const [backdropOpen, setBackdropOpen] = useState(false);
  /**
   * Open backdrop
   */
  const handleBackDropOpen = () => {
    setBackdropOpen(true);
  };
  /**
   * Close backdrop
   */
  const handleBackdropClose = () => {
    setBackdropOpen(false);
  };

  /**
   * ReactEditor settings
   */
  /**
   * ReactEditor parameters
   */
  /* eslint-disable @typescript-eslint/no-unsafe-assignment */
  const ReactEditorJS = createReactEditorJS();
  const [outputBody, setOutputBody] = useState<OutputData>();
  const editorCore = useRef(null);
  /**
   * This is the callback function for initializing ReactEditor
   */
  const handleInitialize = useCallback((instance) => {
    editorCore.current = instance;
  }, []);
  /**
   * This is the callback function for save the output body
   */
  const handleSave = useCallback(async () => {
    try {
      await editorCore.current?.save().then((outputData: OutputData) => {
        setOutputBody(outputData);
      });
    } catch {
      // Editor may have been unmounted (e.g., on CONFIRM/PAYMENT step).
      // outputBody already holds the latest saved data, so we can safely ignore.
    }
  }, [setOutputBody]);

  /**
   * Modal settings
   */
  /**
   * Modal parameters
   */
  const [open, setOpen] = useState(false);
  /**
   * Open the confirmation modal
   */
  const handleClickOpen = () => {
    setOpen(true);
  };
  /**
   * Close the confirmation modal
   */
  const handleClose = () => {
    setOpen(false);
  };

  /**
   * Save Modal settings
   */
  /**
   * Modal parameters
   */
  const [saveOpen, setSaveOpen] = useState(false);
  /**
   * Open the confirmation modal
   */
  const handleClickSaveOpen = () => {
    setSaveOpen(true);
  };
  /**
   * Close the confirmation modal
   */
  const handleSaveClose = () => {
    setSaveOpen(false);
  };

  /**
   * Schedule Modal settings
   */
  /**
   * Modal parameters
   */
  const [scheduleOpen, setScheduleOpen] = useState(false);
  /**
   * Open the schedule modal
   */
  const handleClickScheduleOpen = () => {
    setScheduleOpen(true);
  };
  /**
   * Close the schedule modal
   */
  const handleScheduleClose = () => {
    setScheduleOpen(false);
  };

  /**
   * Final Confirmation Modal settings
   */
  const [finalConfirmOpen, setFinalConfirmOpen] = useState(false);
  const [isScheduledPost, setIsScheduledPost] = useState(false);

  /**
   * Open the final confirmation modal for immediate post
   */
  const handleFinalConfirmOpen = () => {
    setIsScheduledPost(false);
    setFinalConfirmOpen(true);
  };

  /**
   * Open the final confirmation modal for scheduled post
   */
  const handleScheduledFinalConfirmOpen = () => {
    setIsScheduledPost(true);
    setFinalConfirmOpen(true);
  };

  /**
   * Close the final confirmation modal
   */
  const handleFinalConfirmClose = () => {
    setFinalConfirmOpen(false);
  };

  /**
   * Execute the actual article update after final confirmation
   */
  const handleFinalConfirm = () => {
    handleFinalConfirmClose();
    handleBackDropOpen();
    updateArticle("PUBLISHED");
  };

  /**
   * Date validation modal settings
   */
  /**
   * Modal parameters
   */
  const [dateOpen, setDateOpen] = useState(false);
  /**
   * Open the date validation modal
   */
  const handleClickDateOpen = () => {
    setDateOpen(true);
  };
  /**
   * Close the date validation modal
   */
  const handleDateClose = () => {
    setDateOpen(false);
  };

  /**
   * API Response
   */
  const [apiResponse, setApiResponse] = useState<GetArticlesResponse>();

  const onSuccess = (response: GetArticlesResponse) => {
    setApiResponse(response);
    goToCompleteStep();
    props.refetchPublisher();
  };

  /**
   * Update an article
   */
  const { updateArticle } = useUpdateArticle({
    input: {
      editorCore: editorCore,
      articleId: params?.slug || "",
      articleState: article?.articles?.state || "",
      currentThumbnailUrl: article?.articles?.thumbnail_image_url || "",
      publisherId: params?.publisher_id || "",
      title: title,
      outputBody: outputBody,
      country: props.country,
      acceptedFiles: acceptedFiles,
      publishedAt: publishedAt,
      originalPublishedAt: article?.articles?.published_at || "",
      selectedCategories: selectedCategories,
      summary: summary,
      companyInfo: companyInfo,
      mediaContact: mediaContact,
      useThumbnailAsFeaturedImage: thumbnailAsDefault,
    },
    functions: {
      handleBackDropOpen: handleBackDropOpen,
      handleClose: handleClose,
      handleScheduleClose: handleScheduleClose,
      handleBackdropClose: handleBackdropClose,
      handleSave: handleSave,
      handleComplete: onSuccess,
    },
  });

  return (
    <LocalizationProvider dateAdapter={AdapterLuxon}>
      <Backdrop sx={{ color: "#fff", zIndex: 10001 }} open={backdropOpen}>
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* create article modal */}
      <CreateArticleModal
        open={open}
        handleClose={handleClose}
        createArticle={updateArticle}
        country={props.country}
        handleBackDropOpen={handleBackDropOpen}
        is_subscripted={checkSubscripted(props.creditInfo, props.country)}
      />

      {/* create article modal for immediate posting */}
      <CreateArticleModal
        open={open}
        handleClose={handleClose}
        createArticle={async () => {
          // Only show final confirmation if article is not already published or scheduled
          if (
            article?.articles?.state === "published" ||
            article?.articles?.state === "scheduled"
          ) {
            handleBackDropOpen();
            await updateArticle("PUBLISHED");
          } else {
            handleClose();
            handleFinalConfirmOpen();
          }
        }}
        country={props.country}
        handleBackDropOpen={() => {
          // Empty function to satisfy prop requirement
        }}
        is_subscripted={checkSubscripted(props.creditInfo, props.country)}
      />

      {/* save article modal */}
      <SaveArticleModal
        open={saveOpen}
        handleClose={handleSaveClose}
        createArticle={updateArticle}
        handleBackDropOpen={handleBackDropOpen}
      />

      {/* schedule article modal */}
      <ScheduleArticleModal
        open={scheduleOpen}
        handleClose={handleScheduleClose}
        createArticle={async () => {
          // Only show final confirmation if article is not already published or scheduled
          if (
            article?.articles?.state === "published" ||
            article?.articles?.state === "scheduled"
          ) {
            handleBackDropOpen();
            await updateArticle("PUBLISHED");
          } else {
            handleScheduleClose();
            handleScheduledFinalConfirmOpen();
          }
        }}
        handleBackDropOpen={() => {
          // Empty function to satisfy prop requirement
        }}
        publishedAt={publishedAt}
        handlePublishedAtChange={handlePublishedAtChange}
        country={props.country}
      />

      {/* date validation modal */}
      <DateValidationModal
        open={dateOpen}
        handleClose={handleDateClose}
        goToConfirmStep={goToConfirmStep}
      />

      {/* final confirmation modal */}
      <FinalConfirmationModal
        open={finalConfirmOpen}
        handleClose={handleFinalConfirmClose}
        handleConfirm={handleFinalConfirm}
        title={title}
        outputBody={outputBody}
        country={props.country}
        isScheduled={isScheduledPost}
        scheduledDate={isScheduledPost ? publishedAt : undefined}
      />

      {/* Content */}
      <StepPageWrapper>
        {currentStep === "CREATE" ||
        currentStep === "CONFIRM" ||
        currentStep === "PAYMENT" ? (
          <div css={stepperContainer}>
            {currentStep === "CONFIRM" ? (
              <div css={stepperReturnButtonWrapper}>
                <button
                  css={stepperReturnButton}
                  type="button"
                  onClick={goBackToCreateStep}
                >
                  <img
                    width={20}
                    css={stepContent}
                    alt="step1_gray"
                    src="/articleEditor/arrow.png"
                  />
                  <img
                    width={20}
                    css={stepContent}
                    alt="step1_gray"
                    src="/step/step_1_gray.png"
                  />
                  <div>{t("dashboard.table.actions.edit")}</div>
                </button>
              </div>
            ) : null}
            {currentStep === "CREATE" ? (
              <div css={stepperReturnButtonWrapper}>
                <button
                  css={stepperReturnButton}
                  type="button"
                  onClick={goBackToDashboard}
                >
                  <img
                    width={20}
                    css={stepContent}
                    alt="step1_gray"
                    src="/articleEditor/arrow.png"
                  />
                  <div>{t("create_press_release.complete.return.text")}</div>
                </button>
              </div>
            ) : null}
            {currentStep === "PAYMENT" ? (
              <div css={stepperReturnButtonWrapper}>
                <button
                  css={stepperReturnButton}
                  type="button"
                  onClick={goBackToConfirmStep}
                >
                  <img
                    width={20}
                    css={stepContent}
                    alt="step2_gray"
                    src="/articleEditor/arrow.png"
                  />
                  <img
                    width={20}
                    css={stepContent}
                    alt="step2_gray"
                    src="/step/step_2_gray.png"
                  />
                  <div>{t("dashboard.modal.button.confirm")}</div>
                </button>
              </div>
            ) : null}
            {currentStep === "PAYMENT" ? (
              <div css={stepperReturnButtonWrapper}>
                <button
                  css={stepperReturnButton}
                  type="button"
                  onClick={goBackToConfirmStep}
                >
                  <img
                    width={20}
                    css={stepContent}
                    alt="step2_gray"
                    src="/articleEditor/arrow.png"
                  />
                  <img
                    width={20}
                    css={stepContent}
                    alt="step2_gray"
                    src="/step/step_2_gray.png"
                  />
                  <div>{t("dashboard.modal.button.confirm")}</div>
                </button>
              </div>
            ) : null}
            <div css={stepperWrapper}>
              <div css={currentStep === "CREATE" ? step : disabledStep}>
                {currentStep === "CREATE" ? (
                  <img
                    width={30}
                    alt="step1_blue"
                    src="/step/step_1_blue.png"
                  />
                ) : (
                  <img
                    width={30}
                    alt="step1_gray"
                    src="/step/step_1_gray.png"
                  />
                )}
                {t("dashboard.modal.button.edit")}
              </div>
              <div css={currentStep === "CONFIRM" ? step : disabledStep}>
                {currentStep === "CONFIRM" ? (
                  <img
                    width={30}
                    alt="step2_blue"
                    src="/step/step_2_blue.png"
                  />
                ) : (
                  <img
                    width={30}
                    alt="step2_gray"
                    src="/step/step_2_gray.png"
                  />
                )}
                {t("dashboard.modal.button.confirm")}
              </div>
              {article?.articles?.state === "draft" ||
              article?.articles?.state === "payment_failed" ? (
                <div css={currentStep === "PAYMENT" ? step : disabledStep}>
                  {currentStep === "PAYMENT" ? (
                    <img
                      width={30}
                      alt="step3_blue"
                      src="/step/step_3_blue.png"
                    />
                  ) : (
                    <img
                      width={30}
                      alt="step3_gray"
                      src="/step/step_3_gray.png"
                    />
                  )}
                  {t("dashboard.modal.button.payment")}
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
        {currentStep === "CREATE" ? (
          <CreateArticleStep
            // common
            auth={props.auth}
            country={props.country}
            publisherResponse={props.publisherResponse}
            ReactEditorJS={ReactEditorJS}
            // article information
            publishedAt={publishedAt}
            title={title}
            summary={summary}
            selectedCategories={selectedCategories}
            thumbnailAsDefault={thumbnailAsDefault}
            companyInfo={companyInfo}
            mediaContact={mediaContact}
            outputBody={outputBody}
            businessCategories={BUSINESS_CATEGORIES.map((name) => ({
              id: "",
              name,
              name_en: "",
            }))}
            isPublished={
              article?.articles?.state === "published" ||
              article?.articles?.state === "scheduled"
            }
            state={article?.articles?.state}
            thumbnailUrl={article?.articles?.thumbnail_image_url}
            // dropzone param
            acceptedFiles={acceptedFiles}
            isDragAccept={isDragAccept}
            getRootProps={getRootProps}
            getInputProps={getInputProps}
            // functions
            handlePublishedAtChange={handlePublishedAtChange}
            handleBusinessCategories={handleBusinessCategories}
            handleTitleChange={handleTitleChange}
            handleSummaryChange={handleSummaryChange}
            handleThumbnailAsDefault={handleThumbnailAsDefault}
            handleInitialize={handleInitialize}
            handleSave={handleSave}
            handleCompanyInfo={handleCompanyInfo}
            handleMediaContact={handleMediaContact}
            handleClickSaveOpen={handleClickSaveOpen}
            goToConfirmStep={goToConfirmStep}
            handleClickDateOpen={handleClickDateOpen}
          />
        ) : null}
        {currentStep === "CONFIRM" ? (
          <ComfirmArticleStep
            // common
            publisherResponse={props.publisherResponse}
            country={props.country}
            // article information
            publishedAt={publishedAt}
            title={title}
            summary={summary}
            selectedCategories={selectedCategories}
            thumbnailAsDefault={thumbnailAsDefault}
            companyInfo={companyInfo}
            mediaContact={mediaContact}
            outputBody={outputBody}
            businessCategories={BUSINESS_CATEGORIES.map((name) => ({
              id: "",
              name,
              name_en: "",
            }))}
            isPublished={
              article?.articles?.state === "published" ||
              article?.articles?.state === "scheduled"
            }
            thumbnailUrl={article?.articles?.thumbnail_image_url}
            // Dropzone param
            acceptedFiles={acceptedFiles}
            // functions
            handleClickSaveOpen={handleClickSaveOpen}
            updateArticle={() => {
              updateArticle("PUBLISHED");
            }}
            goToPaymentStep={goToPaymentStep}
          />
        ) : null}
        {currentStep === "PAYMENT" ? (
          <PaymentArticleStep
            // common
            credits={props.credits}
            title={title}
            is_subscripted={checkSubscripted(props.creditInfo, props.country)}
            remaining_quota={props.remaining_quota}
            thumbnailUrl={article?.articles?.thumbnail_image_url}
            defaultCountry={props.defaultCountry}
            // functions
            handleSave={handleSave}
            handleClickSaveOpen={handleClickSaveOpen}
            handleClickOpen={handleClickOpen}
            handleClickScheduleOpen={handleClickScheduleOpen}
          />
        ) : null}
        {currentStep === "COMPLETE" ? (
          <CreateCompleteStep
            apiResponse={apiResponse}
            country={props.country}
            publisherResponse={props.publisherResponse}
          />
        ) : null}
      </StepPageWrapper>
    </LocalizationProvider>
  );
};

const stepperContainer = css`
  display: flex;
  justify-content: center;
  position: relative;
  background: #eaeaea;
  height: ${STEPPER_HEIGHT}px;
`;

const stepperWrapper = css`
  display: flex;
  margin-top: 12px;
  @media (max-width: 640px) {
    width: 100%;
    margin-top: 8px;
  }
`;

const step = css`
  background: #ffffff;
  padding: 4px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 180px;
  color: #0045ff;
  @media (max-width: 640px) {
    width: auto;
    flex: 1 1 0;
    min-width: 0;
    padding: 4px 6px;
    font-size: 11px;
    text-align: center;
    & img {
      width: 22px;
    }
  }
`;

const disabledStep = css`
  padding: 4px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 180px;
  color: #969696;
  @media (max-width: 640px) {
    width: auto;
    flex: 1 1 0;
    min-width: 0;
    padding: 4px 6px;
    font-size: 11px;
    text-align: center;
    & img {
      width: 22px;
    }
  }
`;

const stepperReturnButtonWrapper = css`
  position: absolute;
  left: 24px;
  top: 26px;
  @media (max-width: 640px) {
    left: 8px;
    top: 30px;
    font-size: 11px;
  }
`;

const stepperReturnButton = css`
  display: flex;
  align-items: center;
`;

const stepContent = css`
  margin-right: 8px;
`;

export default EditArticlePage;
